import { useState } from "react";

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16, color = "currentColor" }) => {
  const icons = {
    external: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
    book: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
    map: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
    sparkle: <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>,
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    home: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    dollar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    heart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
    tag: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
    x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  };
  return icons[name] || null;
};

// ─── Curated Article Data ─────────────────────────────────────────────────────
// Each entry links to the single best real external article for that topic.

const NEIGHBORHOOD_ARTICLES = [
  {
    id: "nyc-families",
    city: "New York, NY", emoji: "🗽",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)",
    accent: "#e94560",
    title: "Best NYC Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Rankings of the top family-friendly neighborhoods across all five boroughs, scored on schools, crime, cost of living, and family amenities.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/new-york-metro-area/",
    tags: ["Schools", "Safety", "Parks"],
  },
  {
    id: "la-families",
    city: "Los Angeles, CA", emoji: "🌴",
    gradient: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
    accent: "#e84393",
    title: "Best LA Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Data-driven rankings of the safest, highest-rated school districts and most family-friendly communities across Los Angeles County.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/los-angeles-metro-area/",
    tags: ["Schools", "Safety", "Walkability"],
  },
  {
    id: "chicago-families",
    city: "Chicago, IL", emoji: "🏙️",
    gradient: "linear-gradient(135deg, #0f2027 0%, #2c5364 100%)",
    accent: "#00b4d8",
    title: "Best Chicago Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Comprehensive neighborhood rankings across Chicago's 77 community areas, weighted by school quality, crime rates, and family-friendly amenities.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/chicago-metro-area/",
    tags: ["Schools", "Safety", "Affordability"],
  },
  {
    id: "houston-families",
    city: "Houston, TX", emoji: "🤠",
    gradient: "linear-gradient(135deg, #134e5e 0%, #71b280 100%)",
    accent: "#f9ca24",
    title: "Best Houston Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Top-rated family neighborhoods across Houston's sprawling metro, ranked by school district quality, crime statistics, and cost of living.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/houston-metro-area/",
    tags: ["Schools", "Safety", "Value"],
  },
  {
    id: "phoenix-families",
    city: "Phoenix, AZ", emoji: "🌵",
    gradient: "linear-gradient(135deg, #c94b4b 0%, #4b134f 100%)",
    accent: "#f8b500",
    title: "Best Phoenix Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Best places to raise a family in the Phoenix metro, from Scottsdale to Chandler, ranked by schools, safety, and outdoor access.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/phoenix-metro-area/",
    tags: ["Schools", "Safety", "Outdoor Access"],
  },
  {
    id: "philly-families",
    city: "Philadelphia, PA", emoji: "🔔",
    gradient: "linear-gradient(135deg, #2c3e50 0%, #3498db 100%)",
    accent: "#e74c3c",
    title: "Best Philadelphia Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Data-ranked family neighborhoods across Philadelphia and the Main Line suburbs, scored on schools, crime, and community character.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/philadelphia-metro-area/",
    tags: ["Schools", "Safety", "Culture"],
  },
  {
    id: "san-antonio-families",
    city: "San Antonio, TX", emoji: "🌶️",
    gradient: "linear-gradient(135deg, #a8431f 0%, #e8834d 100%)",
    accent: "#ffd166",
    title: "Best San Antonio Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Rankings of the best family-friendly neighborhoods in San Antonio, from Alamo Heights to Stone Oak, scored on school quality and safety.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/san-antonio-metro-area/",
    tags: ["Schools", "Safety", "Affordability"],
  },
  {
    id: "dallas-families",
    city: "Dallas, TX", emoji: "⭐",
    gradient: "linear-gradient(135deg, #1565c0 0%, #00acc1 100%)",
    accent: "#ffd740",
    title: "Best Dallas Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Top family-friendly neighborhoods across Dallas-Fort Worth, ranked by school district ratings, crime statistics, and lifestyle quality.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/dallas-metro-area/",
    tags: ["Schools", "Safety", "Value"],
  },
  {
    id: "austin-families",
    city: "Austin, TX", emoji: "🎸",
    gradient: "linear-gradient(135deg, #004d40 0%, #1b5e20 100%)",
    accent: "#f57f17",
    title: "Best Austin Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Best places to raise a family in Austin and surrounding communities, ranked by school quality, safety, and family amenities.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/austin-metro-area/",
    tags: ["Schools", "Safety", "Outdoor Life"],
  },
  {
    id: "seattle-families",
    city: "Seattle, WA", emoji: "⛵",
    gradient: "linear-gradient(135deg, #005c97 0%, #363795 100%)",
    accent: "#00e676",
    title: "Best Seattle Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Top-ranked family neighborhoods in the Seattle metro, from Bellevue to Issaquah, scored on schools, safety, and quality of life.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/seattle-metro-area/",
    tags: ["Schools", "Safety", "Nature"],
  },
  {
    id: "denver-families",
    city: "Denver, CO", emoji: "🏔️",
    gradient: "linear-gradient(135deg, #6a3093 0%, #a044ff 100%)",
    accent: "#f9a825",
    title: "Best Denver Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Data-driven neighborhood rankings across the Denver metro, covering school quality, crime rates, parks, and family lifestyle.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/denver-metro-area/",
    tags: ["Schools", "Safety", "Outdoor Access"],
  },
  {
    id: "nashville-families",
    city: "Nashville, TN", emoji: "🎵",
    gradient: "linear-gradient(135deg, #7b4f12 0%, #c97b2e 100%)",
    accent: "#FFD700",
    title: "Best Nashville Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Best places to raise a family in Music City and surrounding suburbs, ranked by schools, crime, and community quality.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/nashville-metro-area/",
    tags: ["Schools", "Safety", "Growth"],
  },
  {
    id: "charlotte-families",
    city: "Charlotte, NC", emoji: "🏦",
    gradient: "linear-gradient(135deg, #003087 0%, #0066CC 100%)",
    accent: "#00BFFF",
    title: "Best Charlotte Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Top family-friendly neighborhoods across Charlotte-Mecklenburg, ranked by school quality, crime statistics, and lifestyle.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/charlotte-metro-area/",
    tags: ["Schools", "Safety", "Walkability"],
  },
  {
    id: "portland-families",
    city: "Portland, OR", emoji: "🌹",
    gradient: "linear-gradient(135deg, #2d5016 0%, #4a7c59 100%)",
    accent: "#FF6B35",
    title: "Best Portland Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Best places to raise a family in greater Portland, from established east-side neighborhoods to Lake Oswego and Beaverton.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/portland-metro-area/",
    tags: ["Schools", "Safety", "Parks"],
  },
  {
    id: "boston-families",
    city: "Boston, MA", emoji: "🦞",
    gradient: "linear-gradient(135deg, #8B0000 0%, #CC0000 100%)",
    accent: "#FFD700",
    title: "Best Boston Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Top-ranked family neighborhoods in the Boston metro, from West Roxbury to Newton and Brookline, scored on schools, safety, and history.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/boston-metro-area/",
    tags: ["Schools", "Safety", "Culture"],
  },
  {
    id: "miami-families",
    city: "Miami, FL", emoji: "🏖️",
    gradient: "linear-gradient(135deg, #c0392b 0%, #f39c12 100%)",
    accent: "#00CED1",
    title: "Best Miami Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Data-ranked family neighborhoods in the Miami metro, including Coral Gables, Pinecrest, and Doral, scored on schools and safety.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/miami-metro-area/",
    tags: ["Schools", "Safety", "Culture"],
  },
  {
    id: "minneapolis-families",
    city: "Minneapolis, MN", emoji: "❄️",
    gradient: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
    accent: "#E65100",
    title: "Best Minneapolis Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Top family-friendly neighborhoods in the Twin Cities metro, ranked by school quality, crime rates, parks, and community resources.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/minneapolis-metro-area/",
    tags: ["Schools", "Safety", "Parks"],
  },
  {
    id: "san-diego-families",
    city: "San Diego, CA", emoji: "🌊",
    gradient: "linear-gradient(135deg, #006994 0%, #0099CC 100%)",
    accent: "#FFD700",
    title: "Best San Diego Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Best places to raise a family in San Diego County, from Carmel Valley to Poway, ranked by schools, safety, and outdoor lifestyle.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/san-diego-metro-area/",
    tags: ["Schools", "Safety", "Outdoor Life"],
  },
  {
    id: "tampa-families",
    city: "Tampa, FL", emoji: "⚡",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #8B0000 100%)",
    accent: "#FFC107",
    title: "Best Tampa Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Top family neighborhoods in Tampa Bay — from South Tampa to Wesley Chapel — ranked by schools, crime, and family amenities.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/tampa-metro-area/",
    tags: ["Schools", "Safety", "Value"],
  },
  {
    id: "atlanta-families",
    city: "Atlanta, GA", emoji: "🍑",
    gradient: "linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)",
    accent: "#FF8C00",
    title: "Best Atlanta Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Data-ranked family neighborhoods across metro Atlanta, from Decatur to Dunwoody, scored on school quality, crime, and community.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/atlanta-metro-area/",
    tags: ["Schools", "Safety", "Diversity"],
  },
  {
    id: "las-vegas-families",
    city: "Las Vegas, NV", emoji: "🎰",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #4a0a0a 100%)",
    accent: "#ffd700",
    title: "Best Las Vegas Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Best places to raise a family in the Las Vegas Valley, including Henderson, Summerlin, and Green Valley, ranked by schools and safety.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/las-vegas-metro-area/",
    tags: ["Schools", "Safety", "Affordability"],
  },
  {
    id: "columbus-families",
    city: "Columbus, OH", emoji: "🏈",
    gradient: "linear-gradient(135deg, #8B0000 0%, #C0392B 100%)",
    accent: "#808080",
    title: "Best Columbus Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Top family-friendly neighborhoods in Columbus and surrounding suburbs, ranked by school quality, crime statistics, and lifestyle.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/columbus-metro-area/",
    tags: ["Schools", "Safety", "Value"],
  },
  {
    id: "indianapolis-families",
    city: "Indianapolis, IN", emoji: "🏎️",
    gradient: "linear-gradient(135deg, #002244 0%, #003366 100%)",
    accent: "#C0C0C0",
    title: "Best Indianapolis Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Data-driven rankings of the best family neighborhoods in Indy, from Carmel and Fishers to Broad Ripple, scored on schools and safety.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/indianapolis-metro-area/",
    tags: ["Schools", "Safety", "Affordability"],
  },
  {
    id: "raleigh-families",
    city: "Raleigh, NC", emoji: "🔬",
    gradient: "linear-gradient(135deg, #CC0000 0%, #8B0000 100%)",
    accent: "#B0C4DE",
    title: "Best Raleigh Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Best places to raise a family in the Research Triangle, from North Raleigh to Cary and Apex, ranked by schools and quality of life.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/raleigh-metro-area/",
    tags: ["Schools", "Safety", "Growth"],
  },
  {
    id: "virginia-beach-families",
    city: "Virginia Beach, VA", emoji: "🌅",
    gradient: "linear-gradient(135deg, #003087 0%, #005BBB 100%)",
    accent: "#FFD700",
    title: "Best Virginia Beach Neighborhoods for Families",
    source: "Niche",
    sourceLogo: "N",
    description: "Top family-friendly neighborhoods in Virginia Beach and Hampton Roads, ranked by schools, safety, and coastal lifestyle quality.",
    url: "https://www.niche.com/places-to-live/search/best-neighborhoods-for-families/m/virginia-beach-metro-area/",
    tags: ["Schools", "Safety", "Outdoor Life"],
  },
];

const AFFORDABILITY_ARTICLES = [
  {
    id: "most-affordable-cities-usnews",
    title: "Most Affordable Places to Live in the U.S.",
    source: "U.S. News & World Report",
    sourceLogo: "US",
    emoji: "🏙️",
    gradient: "linear-gradient(135deg, #0f0c29 0%, #302b63 100%)",
    accent: "#a78bfa",
    description: "Comprehensive rankings of the most affordable U.S. cities for quality of life, factoring in cost of living, job market, and livability scores.",
    url: "https://realestate.usnews.com/places/rankings/most-affordable-places-to-live",
    tags: ["Cost of Living", "Jobs", "Quality of Life"],
    isFeatured: true,
  },
  {
    id: "affordable-cities-livability",
    title: "Most Livable Affordable Cities in America",
    source: "Livability",
    sourceLogo: "LV",
    emoji: "🌆",
    gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    accent: "#f9ca24",
    description: "Livability's research-backed rankings of cities that offer the best combination of affordability, walkability, amenities, and community.",
    url: "https://livability.com/best-places/top-100-best-places-to-live/",
    tags: ["Walkability", "Amenities", "Affordability"],
  },
  {
    id: "affordable-cities-money",
    title: "Best Places to Live in America",
    source: "Money Magazine",
    sourceLogo: "$",
    emoji: "💰",
    gradient: "linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%)",
    accent: "#f4a261",
    description: "Money's annual ranking of America's best places to live, weighting cost of living, crime, schools, job growth, and quality of life.",
    url: "https://money.com/best-places-to-live/",
    tags: ["Value", "Schools", "Crime"],
  },
  {
    id: "affordable-cities-bankrate",
    title: "Best & Most Affordable Cities to Live",
    source: "Bankrate",
    sourceLogo: "BR",
    emoji: "📊",
    gradient: "linear-gradient(135deg, #2b5876 0%, #4e4376 100%)",
    accent: "#00d2ff",
    description: "Bankrate's financial analysis of the most affordable U.S. cities with strong job markets, low crime, and high quality-of-life indicators.",
    url: "https://www.bankrate.com/real-estate/best-places-to-live/",
    tags: ["Finance", "Jobs", "Cost of Living"],
  },
  {
    id: "affordable-cities-wallethub",
    title: "Best Cities to Live In",
    source: "WalletHub",
    sourceLogo: "WH",
    emoji: "🏅",
    gradient: "linear-gradient(135deg, #8e0e00 0%, #1f1c18 100%)",
    accent: "#e2c27d",
    description: "WalletHub's 182-city ranking using 53 indicators across affordability, economy, education, health, quality of life, and safety.",
    url: "https://wallethub.com/edu/best-cities-to-live-in/62316",
    tags: ["Multi-Factor", "Education", "Health"],
  },
  {
    id: "affordable-cities-niche-overall",
    title: "Best Cities to Live in America",
    source: "Niche",
    sourceLogo: "N",
    emoji: "🌇",
    gradient: "linear-gradient(135deg, #373B44 0%, #4286f4 100%)",
    accent: "#ff6b6b",
    description: "Niche's data-driven ranking of America's best cities, using grades for crime, schools, cost of living, weather, and commute.",
    url: "https://www.niche.com/places-to-live/search/best-cities/",
    tags: ["Data-Driven", "Schools", "Crime"],
  },
  {
    id: "affordable-midsize-cities",
    title: "Best Affordable Mid-Size Cities",
    source: "SmartAsset",
    sourceLogo: "SA",
    emoji: "📍",
    gradient: "linear-gradient(135deg, #005c97 0%, #363795 100%)",
    accent: "#43e97b",
    description: "SmartAsset's analysis of the most affordable mid-sized American cities with strong employment, low taxes, and high quality of life.",
    url: "https://smartasset.com/mortgage/most-affordable-cities",
    tags: ["Mid-Size", "Taxes", "Employment"],
  },
  {
    id: "affordable-remote-workers",
    title: "Best Affordable Cities for Remote Workers",
    source: "Realtor.com",
    sourceLogo: "R",
    emoji: "💻",
    gradient: "linear-gradient(135deg, #c94b4b 0%, #4b134f 100%)",
    accent: "#00f2fe",
    description: "Realtor.com's ranking of the most affordable cities for remote workers, balancing housing costs, internet infrastructure, and lifestyle.",
    url: "https://www.realtor.com/research/best-affordable-markets-remote-work/",
    tags: ["Remote Work", "Housing", "Lifestyle"],
  },
];

// ─── Components ───────────────────────────────────────────────────────────────

function SourceBadge({ logo, source, accent }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{
        width: 22, height: 22, borderRadius: 7,
        background: accent || "var(--accent)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 9, fontWeight: 900, color: "#fff", flexShrink: 0,
        letterSpacing: "-.02em",
      }}>
        {logo}
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", letterSpacing: ".04em" }}>
        {source}
      </span>
    </div>
  );
}

function ArticleCard({ article }) {
  const [hovered, setHovered] = useState(false);

  return (
    <a
      href={article.url}
      target="_blank"
      rel="noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "block",
        borderRadius: 20,
        overflow: "hidden",
        textDecoration: "none",
        background: "var(--surface)",
        border: "1.5px solid var(--border)",
        boxShadow: hovered ? "0 16px 48px rgba(108,71,255,.18)" : "var(--shadow-card)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition: "all .22s cubic-bezier(0.34, 1.56, 0.64, 1)",
        cursor: "pointer",
      }}
    >
      {/* Colour band */}
      <div style={{
        height: 130, background: article.gradient,
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden",
      }}>
        <span style={{
          fontSize: 56, filter: "drop-shadow(0 4px 12px rgba(0,0,0,.3))",
          transform: hovered ? "scale(1.15) rotate(6deg)" : "scale(1)",
          transition: "transform .3s",
          display: "block",
        }}>
          {article.emoji}
        </span>
        {article.isFeatured && (
          <div style={{
            position: "absolute", top: 10, left: 10,
            background: article.accent, color: "#000",
            fontSize: 9, fontWeight: 800, padding: "3px 10px",
            borderRadius: 50, letterSpacing: ".08em",
          }}>
            ★ FEATURED
          </div>
        )}
        <div style={{
          position: "absolute", top: 10, right: 10,
          background: "rgba(0,0,0,.35)", backdropFilter: "blur(8px)",
          borderRadius: 50, padding: "4px 10px",
          display: "flex", alignItems: "center", gap: 5,
          color: "#fff", fontSize: 10, fontWeight: 600,
        }}>
          <Icon name="external" size={10} color="#fff" />
          {article.source}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "16px 18px 18px" }}>
        <div style={{ marginBottom: 8 }}>
          <SourceBadge logo={article.sourceLogo} source={article.source} accent={article.accent} />
        </div>

        <h3 style={{
          fontSize: 14, fontWeight: 800, fontFamily: "var(--font-display)",
          color: "var(--text)", lineHeight: 1.35, marginBottom: 7,
        }}>
          {article.title}
        </h3>

        <p style={{
          fontSize: 11.5, color: "var(--muted)", lineHeight: 1.65,
          marginBottom: 12,
          display: "-webkit-box", WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {article.description}
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 12 }}>
          {article.tags.map(t => (
            <span key={t} style={{
              fontSize: 9, fontWeight: 700, padding: "3px 9px", borderRadius: 50,
              background: "var(--chip-bg)", color: "var(--muted)",
              border: "1px solid var(--border)", letterSpacing: ".04em",
              textTransform: "uppercase",
            }}>
              {t}
            </span>
          ))}
        </div>

        <div style={{
          display: "flex", alignItems: "center", gap: 5,
          fontSize: 12, fontWeight: 700, color: "var(--accent)",
        }}>
          Read on {article.source}
          <Icon name="external" size={12} color="var(--accent)" />
        </div>
      </div>
    </a>
  );
}

function FeaturedBanner({ article }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", flexDirection: "column", justifyContent: "flex-end",
        borderRadius: 24, overflow: "hidden", textDecoration: "none",
        background: article.gradient,
        minHeight: 220, position: "relative",
        boxShadow: hovered ? "0 20px 56px rgba(108,71,255,.28)" : "0 8px 28px rgba(108,71,255,.12)",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        transition: "all .22s",
        marginBottom: 28,
        cursor: "pointer",
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.82) 0%, rgba(0,0,0,.05) 60%)" }} />
      <div style={{ position: "absolute", right: 40, top: "50%", transform: "translateY(-50%)", fontSize: 110, opacity: .12 }}>
        {article.emoji}
      </div>
      <div style={{ position: "relative", zIndex: 1, padding: "28px 32px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ background: article.accent, color: "#000", fontSize: 10, fontWeight: 800, padding: "4px 12px", borderRadius: 50, letterSpacing: ".08em" }}>
            ★ FEATURED
          </div>
          <span style={{ color: "rgba(255,255,255,.6)", fontSize: 11, fontWeight: 600 }}>{article.source}</span>
        </div>
        <h2 style={{
          fontSize: 22, fontWeight: 900, fontFamily: "var(--font-display)",
          color: "#fff", lineHeight: 1.2, marginBottom: 8, maxWidth: 560
        }}>
          {article.title}
        </h2>
        <p style={{ color: "rgba(255,255,255,.72)", fontSize: 13, lineHeight: 1.6, maxWidth: 520, marginBottom: 18 }}>
          {article.description}
        </p>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          background: article.accent, color: "#000",
          padding: "9px 20px", borderRadius: 50,
          fontSize: 12, fontWeight: 800,
          transform: hovered ? "scale(1.04)" : "scale(1)", transition: "transform .2s",
        }}>
          <Icon name="external" size={13} color="#000" />
          Read on {article.source}
        </div>
      </div>
    </a>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function Explore() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");

  const filters = ["All", "Neighborhoods", "Affordability"];

  const filteredNeighborhood = NEIGHBORHOOD_ARTICLES.filter(a =>
    (activeFilter === "All" || activeFilter === "Neighborhoods") &&
    (searchQuery === "" ||
      a.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredAffordability = AFFORDABILITY_ARTICLES.filter(a =>
    (activeFilter === "All" || activeFilter === "Affordability") &&
    (searchQuery === "" ||
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.source.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const featuredAffordability = filteredAffordability.find(a => a.isFeatured);
  const otherAffordability = filteredAffordability.filter(a => !a.isFeatured);

  const totalResults = filteredNeighborhood.length + filteredAffordability.length;

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <div style={{
        background: "linear-gradient(135deg, #0f0c29 0%, #302b63 60%, #24243e 100%)",
        padding: "44px 32px 40px",
        borderBottom: "1px solid rgba(255,255,255,.06)",
        position: "relative", overflow: "hidden",
      }}>
        {/* Glow orb */}
        <div style={{
          position: "absolute", width: 500, height: 500, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(167,139,255,.18) 0%, transparent 70%)",
          right: -100, top: -100, pointerEvents: "none",
        }} />

        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{
              width: 46, height: 46, borderRadius: 14,
              background: "rgba(167,139,255,.18)", border: "1.5px solid rgba(167,139,255,.35)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Icon name="book" size={21} color="#a78bfa" />
            </div>
            <div>
              <div style={{ fontSize: 10, letterSpacing: ".2em", color: "#a78bfa", fontWeight: 700, textTransform: "uppercase" }}>
                MOVEMETRICS EXPLORE
              </div>
              <div style={{ fontSize: 26, fontWeight: 900, fontFamily: "var(--font-display)", color: "#fff", lineHeight: 1.1 }}>
                City & Neighborhood Guides
              </div>
            </div>
          </div>

          <p style={{ fontSize: 13.5, color: "rgba(255,255,255,.6)", lineHeight: 1.75, maxWidth: 540, marginBottom: 24 }}>
            The best research from across the web — curated and organized for families and movers. Every article links directly to its original source.
          </p>

          {/* Search + filters */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 360 }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                <Icon name="search" size={14} color="rgba(255,255,255,.45)" />
              </span>
              <input
                type="text"
                placeholder="Search city, topic…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: "100%", padding: "10px 16px 10px 40px",
                  background: "rgba(255,255,255,.09)", backdropFilter: "blur(10px)",
                  border: "1.5px solid rgba(255,255,255,.18)", borderRadius: 50,
                  color: "#fff", fontSize: 13, fontFamily: "var(--font-body)", outline: "none",
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", padding: 2,
                    color: "rgba(255,255,255,.5)", display: "flex",
                  }}
                >
                  <Icon name="x" size={13} color="rgba(255,255,255,.5)" />
                </button>
              )}
            </div>

            {filters.map(f => (
              <button key={f} onClick={() => setActiveFilter(f)} style={{
                fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 600,
                padding: "9px 18px", borderRadius: 50, cursor: "pointer",
                border: "1.5px solid " + (activeFilter === f ? "#a78bfa" : "rgba(255,255,255,.18)"),
                background: activeFilter === f ? "rgba(167,139,255,.18)" : "transparent",
                color: activeFilter === f ? "#a78bfa" : "rgba(255,255,255,.55)",
                transition: "all .15s",
              }}>
                {f}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div style={{ display: "flex", gap: 24, marginTop: 22, flexWrap: "wrap" }}>
            {[
              { v: 25, l: "City guides" },
              { v: 8, l: "Affordability guides" },
              { v: "Top", l: "Trusted sources" },
              { v: "Free", l: "Always" },
            ].map(s => (
              <div key={s.l} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: "#a78bfa", fontFamily: "var(--font-display)" }}>{s.v}</span>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,.45)" }}>{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 24px 72px" }}>

        {totalResults === 0 && (
          <div style={{ textAlign: "center", padding: "80px 20px" }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>🔍</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>No results found</div>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>Try searching for a city like "Austin" or a topic like "affordable"</div>
          </div>
        )}

        {/* ─ Affordability section ─ */}
        {filteredAffordability.length > 0 && (
          <section style={{ marginBottom: 44 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 9, marginBottom: 18,
            }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="dollar" size={14} color="var(--accent)" />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--accent)", letterSpacing: ".1em", textTransform: "uppercase" }}>
                Affordable Cities for Quality of Life
              </span>
              <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
              <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 600 }}>{filteredAffordability.length} guides</span>
            </div>

            {featuredAffordability && <FeaturedBanner article={featuredAffordability} />}

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(255px, 1fr))",
              gap: 16,
            }}>
              {otherAffordability.map(a => <ArticleCard key={a.id} article={a} />)}
            </div>
          </section>
        )}

        {/* ─ Neighborhood section ─ */}
        {filteredNeighborhood.length > 0 && (
          <section>
            <div style={{
              display: "flex", alignItems: "center", gap: 9, marginBottom: 18,
            }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="map" size={14} color="var(--accent)" />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--accent)", letterSpacing: ".1em", textTransform: "uppercase" }}>
                Best Neighborhoods by City
              </span>
              <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
              <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 600 }}>{filteredNeighborhood.length} cities</span>
            </div>

            {/* Source note */}
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20,
              background: "var(--chip-bg)", borderRadius: 14, padding: "12px 16px",
              border: "1.5px solid var(--border)",
            }}>
              <Icon name="sparkle" size={14} color="var(--accent)" style={{ flexShrink: 0, marginTop: 1 }} />
              <p style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.65, margin: 0 }}>
                All neighborhood guides link to <strong style={{ color: "var(--text)" }}>Niche.com</strong> — the most comprehensive data-driven neighborhood ranking platform in the U.S., scoring on schools, crime, cost of living, commute, and family amenities. Each link opens the full live rankings for that metro area.
              </p>
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(255px, 1fr))",
              gap: 16,
            }}>
              {filteredNeighborhood.map(a => <ArticleCard key={a.id} article={a} />)}
            </div>
          </section>
        )}

        {/* ─ Bottom CTA ─ */}
        {totalResults > 0 && (
          <div style={{
            marginTop: 48, padding: "24px 28px",
            background: "linear-gradient(135deg, var(--accent-soft) 0%, #f0ebff 100%)",
            borderRadius: 20, border: "1.5px solid var(--accent)",
            display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap",
          }}>
            <div style={{ width: 46, height: 46, borderRadius: 14, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="home" size={22} color="#fff" />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 15, fontWeight: 800, fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 3 }}>
                Found your city? Now find your home.
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.65 }}>
                Use MoveMetrics Home Search to find real listings — with filters for commute, schools, daycare, and more.
              </div>
            </div>
            <button
              onClick={() => { try { window.location.hash = "homes"; window.location.reload(); } catch {} }}
              style={{
                background: "var(--accent)", color: "#fff", border: "none",
                borderRadius: 50, padding: "11px 22px",
                fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 700,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
                boxShadow: "0 4px 16px rgba(108,71,255,.3)", flexShrink: 0,
              }}
            >
              <Icon name="home" size={14} color="#fff" />
              Search Listings
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
